import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { House } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";

export default function BookingPaypal() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const bookingId = params.get("booking");
  const [config, setConfig] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!bookingId) { setError(true); return; }
    api.get("/config/paypal").then((r) => setConfig(r.data)).catch(() => setError(true));
    api.get(`/bookings/${bookingId}/instructions`)
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, [bookingId]);

  const money = (v) => new Intl.NumberFormat(i18n.language, { style: "currency", currency: "EUR" }).format(v);

  return (
    <div className="min-h-screen bg-sand text-ink">
      <Navbar />
      <main className="pt-32 pb-24 md:pt-40">
        <div className="max-w-xl mx-auto px-6">
          {error && <p data-testid="paypal-error" className="text-center text-ink/60">{t("instr.error")}</p>}
          {!error && (!data || !config) && <p className="text-center text-ink/50">{t("instr.loading")}</p>}
          {data && config && (
            <div data-testid="paypal-card" className="bg-white border border-line p-8 md:p-10">
              <p className="text-xs uppercase tracking-[0.3em] text-terracotta">PayPal</p>
              <h1 className="mt-3 font-serif text-3xl md:text-4xl">{t("paypal.title")}</h1>
              <p className="mt-2 text-sm text-ink/60">{data.booking.apartment_name} · {data.booking.checkin} → {data.booking.checkout}</p>
              <p className="mt-4 font-serif text-3xl" data-testid="paypal-amount">{money(data.booking.total_charged)}</p>

              {config.enabled ? (
                <div className="mt-8" data-testid="paypal-buttons">
                  <PayPalScriptProvider options={{ clientId: config.client_id, currency: "EUR", locale: "es_ES" }}>
                    <PayPalButtons
                      style={{ layout: "vertical", color: "gold", shape: "rect", label: "pay" }}
                      createOrder={async () => {
                        const r = await api.post(`/paypal/${bookingId}/create-order`);
                        return r.data.id;
                      }}
                      onApprove={async (payload) => {
                        try {
                          const r = await api.post(`/paypal/${bookingId}/capture/${payload.orderID}`);
                          if (r.data.status === "COMPLETED") {
                            navigate(`/reserva/exito?booking=${bookingId}`);
                          } else {
                            toast.error(t("paypal.notCompleted"));
                          }
                        } catch (e) {
                          toast.error(t("paypal.error"));
                        }
                      }}
                      onError={() => toast.error(t("paypal.error"))}
                    />
                  </PayPalScriptProvider>
                </div>
              ) : (
                <p data-testid="paypal-not-configured" className="mt-8 border border-terracotta/40 bg-terracotta/5 px-4 py-3 text-xs leading-relaxed text-terracotta">
                  {t("paypal.notConfigured")}
                </p>
              )}

              <Link data-testid="paypal-home" to="/" className="mt-8 flex items-center justify-center gap-2 py-4 border border-ink/20 text-ink text-xs uppercase tracking-[0.15em] hover:bg-ink hover:text-sand transition-colors">
                <House size={15} /> {t("success.home")}
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
